"use client";

import React, { useEffect } from "react";
import { Formik, Form } from "formik";
import {
  insertOrUpdatePaymentMethod,
  findAllPaymentMethod,
  findByIDPaymentMethod,
} from "./paymentMethod.service";
import { paymentMethodValidationSchema } from "./paymentMethod.validation";
import { clearPaymentMethodDetails } from "./paymentMethod.slice";
import { closed } from "@/redux/slices/drawerSlice";
import { STATUS_OPTIONS } from "@/lib/constants";
import { InputField } from "@/components/common/FormFields/InputField";
import { SelectField } from "@/components/common/FormFields/SelectField";
import { ImageUploadField } from "@/components/common/FormFields/ImageUploadField";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { CustomButton } from "@/components/common/CustomButton";
import { Save, X, CreditCard, Camera } from "lucide-react";
import { FormLoader } from "@/components/common/FormLoader";

const PaymentMethodForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const { PaymentMethodDetail, formLoading } = useAppSelector(
    (state) => state.paymentMethod,
  );
  const { data: PaymentMethodID } = useAppSelector((state) => state.drawer);

  useEffect(() => {
    if (PaymentMethodID) {
      dispatch(findByIDPaymentMethod(PaymentMethodID));
    }
  }, [PaymentMethodID, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearPaymentMethodDetails());
    };
  }, [dispatch]);

  const handleSubmit = async (values: any) => {
    const formData = new FormData();
    formData.append("ID", (PaymentMethodID || "0").toString());
    formData.append("UPI", values.UPI);
    formData.append("BankAccountName", values.BankAccountName);
    formData.append("QRCodeImagePath", values.QRCodeImagePath);

    if (values.QRCodeImage instanceof File) {
      formData.append("QRCodeImage", values.QRCodeImage);
    }

    await dispatch(insertOrUpdatePaymentMethod(formData));
    dispatch(findAllPaymentMethod());
    dispatch(closed());
  };

  return (
    <div className="relative">
      <FormLoader loading={formLoading} />
      <Formik
        initialValues={{
          UPI: PaymentMethodDetail.UPI || "",
          BankAccountName: PaymentMethodDetail.BankAccountName || "",
          QRCodeImage: PaymentMethodDetail.QRCodeImage || "",
          QRCodeImagePath: PaymentMethodDetail.QRCodeImagePath || "",
        }}
        validationSchema={paymentMethodValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {() => (
          <Form className="flex flex-col gap-6">
            <div className="space-y-6">
              <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100 space-y-4">
                <div className="flex items-center gap-2 text-slate-800 font-bold uppercase tracking-wider text-[10px] mb-2">
                  <CreditCard size={14} className="text-gold-dim" /> 
                  Payment Details
                </div>
                
                <InputField
                  label="UPI ID"
                  name="UPI"
                  placeholder="example@upi"
                  required
                />

                <InputField
                  label="Bank Account Name"
                  name="BankAccountName"
                  placeholder="Enter account holder name"
                  required
                />
              </div>

              <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100 space-y-4">
                <div className="flex items-center gap-2 text-slate-800 font-bold uppercase tracking-wider text-[10px] mb-2">
                  <Camera size={14} className="text-gold-dim" /> 
                  QR Code Image
                </div>
                <ImageUploadField 
                  label="QR Code" 
                  name="QRCodeImage" 
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-6 mt-6 border-t border-slate-100">
              <CustomButton
                type="submit"
                variant="primary"
                loading={formLoading}
                icon={<Save size={14} />}
                fullWidth
              >
                {PaymentMethodID ? "Update" : "Insert"}
              </CustomButton>
              <CustomButton
                type="button"
                variant="outline"
                icon={<X size={14} />}
                onClick={() => dispatch(closed())}
                disabled={formLoading}
                fullWidth
              >
                Cancel
              </CustomButton>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default PaymentMethodForm;
